import { Box, Grid, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from 'store/AppStore';
import {
  getListOfValues,
  getProductHierarchy,
  getProperties,
  setIsDataUpdated,
} from '../../store/states/PdmSlice';
import {
  ECommonDialogType,
  EDataTableType,
  EMasterPropertyCodes,
  EPropertyApplicability,
  EPropertyGroup,
  RecordLimit,
} from '../../data/Constants';
import { useTranslation } from 'react-i18next';
import { openDialog } from '../../store/states/DialogSlice';
import { PropertiesSection } from './PropertiesSection';
import { HistoryDialog } from './HistoryDialog';
import { PdmApi } from '../../api/PdmApi';
import { getPropAssocRequest, removeEmptyValues } from '../../services/PropertyHelperFunctions';
import { HistoryButton, RefreshButton } from '../IconTypes';

import type {
  IProperty,
  IPropertyAssocProps,
  IPropertyAssociation,
  IHistoryDialogState,
} from '../../../types';
import type { IReadPropAssociations, ISavePropAssociations } from '../../../types/IRequestTypes';

//Filter properties based on applicability
//For Master properties, include both MSTR and NMSTR properties
const getFilteredProperties = (
  properties: IProperty[],
  applicability: EPropertyApplicability,
  type?: string
) => {
  return properties?.filter((prop) => {
    if (applicability === EPropertyApplicability.Master) {
      // Include NMSTR properties only for numeric feature types (NUM)
      return (
        prop.applicability.includes(EPropertyApplicability.Master) ||
        (type === EPropertyApplicability.Numeric &&
          prop.applicability.includes(EPropertyApplicability.NumericMaster)) ||
        (type === EPropertyApplicability.Product &&
          prop.applicability.includes(EPropertyApplicability.NumericMaster) &&
          prop.applicability.includes(EPropertyApplicability.Product))
      );
    } else if (applicability === EPropertyApplicability.Product) {
      // Exclude NMSTR properties for product level
      return (
        prop.applicability.includes(EPropertyApplicability.Product) &&
        !prop.applicability.includes(EPropertyApplicability.NumericMaster) &&
        !prop.applicability.includes(EPropertyApplicability.Master)
      );
    }
    return prop.applicability.includes(applicability);
  });
};

/**
 * Returns the Property association Form fields to be updated and saved
 * @param {IPropertyAssocProps} props selected Item
 * @returns {React.JSX.Element} JSX Element
 */
export const PropertyAssociation = (props: IPropertyAssocProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const [editMasterProps, setEditMasterProps] = useState<boolean>(false);
  const [editProductProps, setEditProductProps] = useState<boolean>(false);
  const [isMasterUpdated, setIsMasterUpdated] = useState<boolean>(false);
  const [isProductUpdated, setIsProductUpdated] = useState<boolean>(false);
  const [masterPropertyAssoc, setMasterPropertyAssoc] = useState<{
    [key: string]: string | null;
  }>();
  const [wipMasterPropertyAssoc, setWipMasterPropertyAssoc] = useState<{
    [key: string]: string | null;
  }>();
  const [productPropertyAssoc, setProductPropertyAssoc] = useState<{
    [key: string]: string | null;
  }>();
  const [wipProductPropertyAssoc, setWipProductPropertyAssoc] = useState<{
    [key: string]: string | null;
  }>();
  const [initialMasterPropertyAssoc, setInitialMasterPropertyAssoc] = useState<{
    [key: string]: string | null;
  }>();
  const [initialProductPropertyAssoc, setInitialProductPropertyAssoc] = useState<{
    [key: string]: string | null;
  }>();

  const [historyDialog, setHistoryDialog] = useState<IHistoryDialogState>({
    material: { ...props.material },
    tableType: '' as EDataTableType,
    open: false,
    property: undefined,
  });

  const pdm = useSelector((state: AppState) => state.pdm);
  const properties = pdm.properties;
  const listOfValues = pdm.listOfValues;
  const isModified = pdm.isModified;

  const showPropertyHistoryDialog = (
    tableType: EDataTableType | '',
    open: boolean,
    property?: IProperty
  ) => {
    setHistoryDialog({
      ...historyDialog,
      material: { ...props.material },
      tableType: tableType,
      open: open,
      property: property,
    });
  };

  /**
   * This function call getProductHierarchy and getListOfValue api call
   */

  const loadListOfValues = async () => {
    // Hierarchy and LOV api cannot be called in parallel as lov api depend on hierarchy data
    await dispatch(getProductHierarchy());
    await dispatch(getListOfValues()); //Gets List Of Values
  };

  useEffect(() => {
    if (properties.length > 0 && listOfValues.length === 0) {
      loadListOfValues();
      setInitialData();
    }
  }, [properties]);

  /**
   * set Property associations for particular material
   * As soon as this function trigger and call the get getPropertyAssociations.
   * It format the response in in the required format and set the state.
   */

  const setPropertyAssoc = async () => {
    //Call read property association API only after properties and values at master level fetched.
    if (properties?.length > 0) {
      const payload: IReadPropAssociations = getPropAssocRequest(props.material);
      payload.includeFeature = false;
      const res = await PdmApi.getPropertyAssociations(payload, dispatch);
      if (res.data) {
        const masterPropAssoc: { [key: string]: string | null } = {};
        const productPropAssoc: { [key: string]: string | null } = {};
        res.data[0].associations.map((assoc) => {
          if (assoc.code === EMasterPropertyCodes.AgCode) {
            assoc.value = JSON.stringify([assoc.value]);
          }
          const property = properties.find((p) => p.code === assoc.code);
          // Check if property belongs to Master or NumericMaster (for Numeric material types)
          const isMasterProperty =
            property?.applicability.includes(EPropertyApplicability.Master) ||
            property?.applicability.includes(EPropertyApplicability.NumericMaster);
          if (isMasterProperty) {
            masterPropAssoc[assoc.code] = assoc.value;
          } else {
            productPropAssoc[assoc.code] = assoc.value;
          }
        });

        setInitialMasterPropertyAssoc(masterPropAssoc);
        setMasterPropertyAssoc(masterPropAssoc);

        //set Product associations
        setInitialProductPropertyAssoc(productPropAssoc);
        setProductPropertyAssoc(productPropAssoc);
      } else {
        // Clear state on error to prevent displaying stale data for the newly selected feature/option
        setInitialMasterPropertyAssoc({});
        setMasterPropertyAssoc({});

        setInitialProductPropertyAssoc({});
        setProductPropertyAssoc({});
      }
    }
  };

  /**
   * Reset the state to the initail state
   */

  const setInitialData = () => {
    //Read Property associations for a Product/Feature/Option
    setPropertyAssoc();
    setWipMasterPropertyAssoc({});
    setWipProductPropertyAssoc({});
    dispatch(setIsDataUpdated(false)); //Selected Item changes, data updated is false
    setEditMasterProps(false); //Selected Item changes, master Props edit is made false
    setEditProductProps(false); //Selected Item changes, product Props edit is made false
    setIsMasterUpdated(false); //Selected Item changes, master Props update is made false
    setIsProductUpdated(false); //Selected Item changes, product Props update is made false
  };

  useEffect(() => {
    setInitialData(); //Sets initial data
  }, [props.material.code]);

  //Handle Form field input change for Master props
  const handleMasterInputChange = (code: string, value) => {
    if (!isModified) {
      dispatch(setIsDataUpdated(true));
    }
    setIsMasterUpdated(true);
    //This state is used to display data in UI
    setMasterPropertyAssoc({ ...masterPropertyAssoc, [code]: value ? value.toString() : '' });
    //This state is send as payload on save
    setWipMasterPropertyAssoc({ ...wipMasterPropertyAssoc, [code]: value ? value.toString() : '' });
  };

  //Handle Form field input change for Product props
  const handleProductInputChange = (code: string, value) => {
    if (!isModified) {
      dispatch(setIsDataUpdated(true));
    }
    setIsProductUpdated(true);
    //This state is used to display data in UI
    setProductPropertyAssoc({ ...productPropertyAssoc, [code]: value ? value.toString() : '' });
    //This state is send as payload on save
    setWipProductPropertyAssoc({
      ...wipProductPropertyAssoc,
      [code]: value ? value.toString() : '',
    });
  };

  //Handle Save functionality
  /**
   * Once the changes are done, on click of save button this function trigger
   * Before saving the changes, it's it's prepare the value for the payload.
   * @param propType is type of master or product
   */

  const handleSave = async (propType: EPropertyGroup) => {
    const request: ISavePropAssociations = getPropAssocRequest(props.material);
    const associations: IPropertyAssociation[] = [];
    const propertyAssociation =
      propType === EPropertyGroup.Master ? wipMasterPropertyAssoc : wipProductPropertyAssoc;
    if (propertyAssociation) {
      Object.keys(propertyAssociation).forEach((assoc: string) => {
        let value = propertyAssociation?.[assoc];
        if (value !== '' && assoc === EMasterPropertyCodes.AgCode) {
          const parsedValue = JSON.parse(value);
          if (typeof parsedValue !== 'string') {
            value = parsedValue[0];
          }
        }
        associations.push({
          code: assoc,
          value: value,
        });
      });
    }
    request.properties = associations;
    //Save associations call
    const res = await PdmApi.savePropertyAssociations(request, dispatch);
    if (res && !res.error) {
      if (propType === EPropertyGroup.Master) {
        //Clear the empty values from association before set the state, so that it will not override the default value in UI
        const cleanedMasterAssoc = masterPropertyAssoc
          ? removeEmptyValues(masterPropertyAssoc)
          : {};
        setInitialMasterPropertyAssoc(cleanedMasterAssoc);
        setMasterPropertyAssoc(cleanedMasterAssoc);
        setEditMasterProps(false);
        setIsMasterUpdated(false);
      } else {
        //Clear the empty values from association before set the state, so that it will not override the default value in UI
        const cleanedProductAssoc = productPropertyAssoc
          ? removeEmptyValues(productPropertyAssoc)
          : {};
        setInitialProductPropertyAssoc(cleanedProductAssoc);
        setProductPropertyAssoc(cleanedProductAssoc);
        setEditProductProps(false);
        setIsProductUpdated(false);
      }
    }
  };

  //undo master associations
  const undoMasterAssociations = () => {
    setWipMasterPropertyAssoc({});
    if (!isProductUpdated) {
      //Set master isDataUpdated to false only when both product and master props are not updated
      dispatch(setIsDataUpdated(false));
    }
    setMasterPropertyAssoc(initialMasterPropertyAssoc);
    setEditMasterProps(false);
    setIsMasterUpdated(false);
  };

  //Undo product associations
  const undoProductAssociations = () => {
    setWipProductPropertyAssoc({});
    if (!isMasterUpdated) {
      //Set master isDataUpdated to false only when both product and master props are not updated
      dispatch(setIsDataUpdated(false));
    }
    setProductPropertyAssoc(initialProductPropertyAssoc);
    setEditProductProps(false);
    setIsProductUpdated(false);
  };

  //When clicked on cancel button
  const handleCancel = (propType: EPropertyGroup) => {
    //Master Props cancel button click
    if (propType === EPropertyGroup.Master) {
      if (isMasterUpdated) {
        //if data is updated, show warning dialog else switch to view mode
        dispatch(
          openDialog({
            type: ECommonDialogType.Confirm,
            title: t('dialogBox.unsavedChanges'),
            data: t('messages.cancel.undoPropAssociationWarning'),
            handleClickOk: undoMasterAssociations,
          })
        );
      } else {
        setEditMasterProps(false);
      } //Product Props cancel button click
    } else if (isProductUpdated) {
      //if data is updated, show warning dialog else switch to view mode
      dispatch(
        openDialog({
          type: ECommonDialogType.Confirm,
          title: t('dialogBox.unsavedChanges'),
          data: t('messages.cancel.undoPropAssociationWarning'),
          handleClickOk: undoProductAssociations,
        })
      );
    } else {
      setEditProductProps(false);
    }
  };

  /**
   * This function call the getProperties and loadListOfValues, loadListOfValues call once the getProperties capi call completed.
   * Once data load completed it set's the data to the initail values.
   */

  const handleConfirmRefresh = () => {
    dispatch(getProperties({ page: 1, limit: RecordLimit.default })).then(() => {
      loadListOfValues();
    });
    setInitialData();
  };

  //When refresh icon is clicked
  const handleRefresh = () => {
    if (isModified) {
      dispatch(
        openDialog({
          type: ECommonDialogType.Confirm,
          title: t('dialogBox.unsavedChanges'),
          data: t('messages.refresh'),
          handleClickOk: handleConfirmRefresh,
        })
      );
    } else {
      handleConfirmRefresh();
    }
  };

  return (
    <Box className="properties-div">
      <div className="d-flex align-center">
        <h3 className="properties-pane-title" data-testid="properties-title">
          {props.material.label}
        </h3>
        <RefreshButton handleRefresh={handleRefresh} />
        <Tooltip title={t('labels.history')}>
          <span>
            <HistoryButton
              showPropertyHistoryDialog={() =>
                showPropertyHistoryDialog(EDataTableType.AllPropertiesHistory, true)
              }
            />
          </span>
        </Tooltip>
      </div>
      <Grid container spacing={1} className="h-100">
        <Grid key="MasterProps" data-testid="master-properties" className="pt-0 h-100" size={6}>
          <PropertiesSection
            label={t('labels.masterProperties')}
            propertyVisibility={EPropertyGroup.Master}
            editProperties={editMasterProps}
            properties={getFilteredProperties(
              properties,
              EPropertyApplicability.Master,
              props.material.type
            )}
            listOfValues={listOfValues}
            associations={masterPropertyAssoc}
            isDataUpdated={isMasterUpdated}
            setEditProperties={setEditMasterProps}
            handleSave={handleSave}
            handleCancel={handleCancel}
            handleInputChange={handleMasterInputChange}
            material={props.material}
            showPropertyHistoryDialog={showPropertyHistoryDialog}
          />
        </Grid>
        <Grid key="ProductProps" data-testid="product-properties" className="pt-0 h-100" size={6}>
          <PropertiesSection
            label={t('labels.productProperties')}
            propertyVisibility={EPropertyGroup.Product}
            editProperties={editProductProps}
            properties={getFilteredProperties(properties, props.material.type)}
            listOfValues={listOfValues}
            associations={productPropertyAssoc}
            isDataUpdated={isProductUpdated}
            setEditProperties={setEditProductProps}
            handleSave={handleSave}
            handleCancel={handleCancel}
            handleInputChange={handleProductInputChange}
            material={props.material}
            showPropertyHistoryDialog={showPropertyHistoryDialog}
          />
        </Grid>
      </Grid>
      {historyDialog.open && (
        <HistoryDialog
          historyDialog={historyDialog}
          onClose={() => showPropertyHistoryDialog('', false)}
        />
      )}
    </Box>
  );
};
